import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";
import DocumentEntryDialog from "./DocumentEntryDialog";
import { SiteFilterSelect } from "./shared/SiteFilterSelect";
import { NoAccess } from "../equipment/shared/NoAccess";
import { PaginationBar } from "@/components/shared/PaginationBar";

const HO_ROLES = ["Director", "Manager", "Back Office"];

function SeView() {
  const [entries, setEntries] = useState([]);
  const [displayLabel, setDisplayLabel] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [creating, setCreating] = useState(false);
  const [viewId, setViewId] = useState(null);

  const load = (pageNum = 1) => {
    client
      .get("/document-register/mine", { params: { page: pageNum, page_size: 50 } })
      .then((res) => {
        setEntries(res.data.items);
        setDisplayLabel(res.data.display_label);
        setTotalPages(res.data.total_pages);
        setPage(res.data.page);
        setLoaded(true);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  useEffect(() => load(1), []);

  if (!loaded) return <p className="py-8 text-center text-slate-400">Loading...</p>;

  return (
    <div className="space-y-4" data-testid="document-se-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">
          Site: <span className="font-medium text-slate-700">{displayLabel}</span>
        </p>
        <Button data-testid="document-add-button" onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="h-4 w-4 mr-1.5" /> Add Document Entry
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="document-se-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Files</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-slate-400">
                  No document entries yet.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`document-se-row-${e.id}`} className="cursor-pointer" onClick={() => setViewId(e.id)}>
                  <TableCell>{new Date(e.date).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="text-sm max-w-[400px] truncate">{e.description}</TableCell>
                  <TableCell>{(e.files || []).length}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="document-se-pagination" />

      {creating && <DocumentEntryDialog entryId={null} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(1); }} />}
      {viewId && <DocumentEntryDialog entryId={viewId} onClose={() => setViewId(null)} onSaved={() => { setViewId(null); load(page); }} />}
    </div>
  );
}

function HoView() {
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewId, setViewId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    client.get("/document-register/sites").then((res) => setSites(res.data)).catch(() => {});
  }, []);

  const siteLabels = useMemo(() => Object.fromEntries(sites.map((s) => [s.code, s.display_label || s.label])), [sites]);

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { include_inactive: includeInactive, page: pageNum, page_size: 50 };
    if (siteFilter) params.site = siteFilter;
    if (search.trim()) params.search = search.trim();
    client
      .get("/document-register", { params })
      .then((res) => {
        setEntries(res.data.items);
        setTotalPages(res.data.total_pages);
        setPage(res.data.page);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFilter, search, includeInactive]);

  return (
    <div className="space-y-4" data-testid="document-ho-view">
      <div className="flex flex-wrap items-center gap-3">
        <SiteFilterSelect sites={sites} value={siteFilter} onChange={setSiteFilter} testId="document-site-filter" placeholder="All sites" allowAll />
        <Input
          data-testid="document-search-input"
          placeholder="Search description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
        />
        <button
          data-testid="document-include-inactive-toggle"
          onClick={() => setIncludeInactive((v) => !v)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            includeInactive ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {includeInactive ? "Showing All" : "Showing Active Only"}
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="document-register-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Files</TableHead>
              <TableHead>Entered By</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  No document entries found.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`document-row-${e.id}`} className="cursor-pointer" onClick={() => setViewId(e.id)}>
                  <TableCell>{new Date(e.date).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="text-xs">{siteLabels[e.site] || e.site}</TableCell>
                  <TableCell className="text-sm max-w-[320px] truncate">{e.description}</TableCell>
                  <TableCell>{(e.files || []).length}</TableCell>
                  <TableCell className="text-xs">{e.entered_by_name || e.entered_by}</TableCell>
                  <TableCell>
                    <Badge className={e.active === false ? "bg-slate-400" : "bg-emerald-600"}>{e.active === false ? "Inactive" : "Active"}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="document-ho-pagination" />

      {viewId && <DocumentEntryDialog entryId={viewId} onClose={() => setViewId(null)} onSaved={() => { setViewId(null); load(page); }} />}
    </div>
  );
}

export default function DocumentRegisterTab() {
  const { user } = useAuth();
  const isSE = user?.role === "Site Engineer";
  const isHO = HO_ROLES.includes(user?.role);
  if (!isSE && !isHO) return <NoAccess />;
  return <div data-testid="document-register-tab">{isSE ? <SeView /> : <HoView />}</div>;
}
